'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';
import { isServer } from '@/app/utils/browser';
import { useMediaQuery } from '@/components/hooks/useMediaQuery';
export interface RevealAnimOpts {
  groupOnce?: boolean;
  itemOnce?: boolean;
  groupMargin?: string;
  itemMargin?: string;
  groupAmount?: number;
  itemAmount?: number;
  defaultStagger?: number;
  defaultBaseDelay?: number;
  enabled?: boolean;
}

/**
 * - Group container: [data-revealanim-group] (+ optional: data-stagger, data-delay-base)
 * - Child item:      [data-revealanim] (+ optional: data-order)
 * - Standalone item: [data-revealanim] (+ optional: data-delay)
 */
export const useRevealAnim = opts => {
  const $ = _c(14);
  let t0;
  if ($[0] !== opts) {
    t0 = opts ?? {};
    $[0] = opts;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    groupOnce: t1,
    itemOnce: t2,
    groupMargin: t3,
    itemMargin: t4,
    groupAmount: t5,
    itemAmount: t6,
    defaultStagger: t7,
    defaultBaseDelay: t8,
    enabled: t9
  } = t0;
  const groupOnce = t1 === undefined ? true : t1;
  const itemOnce = t2 === undefined ? true : t2;
  const groupMargin = t3 === undefined ? "0px 0px 0px 0px" : t3;
  const itemMargin = t4 === undefined ? "0px 0px 0px 0px" : t4;
  const groupAmount = t5 === undefined ? 0.25 : t5;
  const itemAmount = t6 === undefined ? 0.25 : t6;
  const defaultStagger = t7 === undefined ? 0.08 : t7;
  const defaultBaseDelay = t8 === undefined ? 0 : t8;
  const enabled = t9 === undefined ? true : t9;
  const isReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)") ?? false;
  let t10;
  let t11;
  if ($[2] !== defaultBaseDelay || $[3] !== defaultStagger || $[4] !== enabled || $[5] !== groupAmount || $[6] !== groupMargin || $[7] !== groupOnce || $[8] !== isReducedMotion || $[9] !== itemAmount || $[10] !== itemMargin || $[11] !== itemOnce) {
    t10 = () => {
      if (!enabled || isServer) {
        return;
      }
      const groups = Array.from(document.querySelectorAll("[data-revealanim-group]"));
      const groupIO = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          const group = entry.target as HTMLElement;
          const visible = entry.isIntersecting || entry.intersectionRatio >= groupAmount;
          if (visible) {
            const stagger = Number(group.dataset.stagger ?? defaultStagger) || 0;
            const base = Number(group.dataset.delayBase ?? defaultBaseDelay) || 0;
            group.style.setProperty("--stagger", `${stagger}s`);
            group.style.setProperty("--delay-base", `${base}s`);
            group.setAttribute("data-revealanim-group-visible", "1");
            const items = Array.from(group.querySelectorAll("[data-revealanim]"));
            items.forEach((el, idx) => {
              const order = Number(el.dataset.order ?? idx);
              el.style.setProperty("--i", String(order));
              if (!isReducedMotion) {
                el.style.removeProperty("transitionDelay");
              }
              el.setAttribute("data-revealanim-visible", "1");
            });
            if (groupOnce) {
              groupIO.unobserve(group);
            }
          } else {
            if (!groupOnce) {
              group.removeAttribute("data-revealanim-group-visible");
              const items_0 = group.querySelectorAll("[data-revealanim]");
              items_0.forEach(_temp);
            }
          }
        });
      }, {
        root: null,
        rootMargin: groupMargin,
        threshold: groupAmount
      });
      groups.forEach(g => groupIO.observe(g));
      const allRevealElements = Array.from(document.querySelectorAll("[data-revealanim]"));
      const standalone = allRevealElements.filter(_temp2);
      const itemIO = new IntersectionObserver(entries_0 => {
        entries_0.forEach(entry_0 => {
          const el_2 = entry_0.target as HTMLElement;
          const visible_0 = entry_0.isIntersecting || entry_0.intersectionRatio >= itemAmount;
          if (visible_0) {
            const delay = Number(el_2.dataset.delay ?? 0) || 0;
            if (!isReducedMotion) {
              el_2.style.transitionDelay = `${delay}s`;
            }
            el_2.setAttribute("data-revealanim-visible", "1");
            if (itemOnce) {
              itemIO.unobserve(el_2);
            }
          } else {
            if (!itemOnce) {
              el_2.removeAttribute("data-revealanim-visible");
              el_2.style.removeProperty("transitionDelay");
            }
          }
        });
      }, {
        root: null,
        rootMargin: itemMargin,
        threshold: itemAmount
      });
      standalone.forEach(el_3 => itemIO.observe(el_3));
      return () => {
        groupIO.disconnect();
        itemIO.disconnect();
      };
    };
    t11 = [enabled, groupOnce, itemOnce, groupMargin, itemMargin, groupAmount, itemAmount, defaultStagger, defaultBaseDelay, isReducedMotion];
    $[2] = defaultBaseDelay;
    $[3] = defaultStagger;
    $[4] = enabled;
    $[5] = groupAmount;
    $[6] = groupMargin;
    $[7] = groupOnce;
    $[8] = isReducedMotion;
    $[9] = itemAmount;
    $[10] = itemMargin;
    $[11] = itemOnce;
    $[12] = t10;
    $[13] = t11;
  } else {
    t10 = $[12];
    t11 = $[13];
  }
  useEffect(t10, t11);
};
function _temp(el_0) {
  el_0.removeAttribute("data-revealanim-visible");
  el_0.style.removeProperty("--i");
  el_0.style.removeProperty("transitionDelay");
}
function _temp2(el_1) {
  return !el_1.closest("[data-revealanim-group]");
}